import { useCallback, useEffect, useState } from 'react'

/**
 * Fullscreen, with a fallback for the browsers that refuse it.
 *
 * Two separate things are going on and they are deliberately not the same switch:
 *
 *  - the Fullscreen API, which hides the BROWSER's own chrome (on a phone, the address bar and
 *    the status bar — well over a hundred pixels of a short screen),
 *  - `isImmersive`, which is ours, and hides the app's own header and rail.
 *
 * They are separate because the first one is not always available. Safari on iPhone has never
 * implemented `requestFullscreen` on an ordinary element, so on the single most likely device for
 * "make this easier to see on mobile" the API does nothing at all. If the two were one flag, the
 * button would be dead on iOS. Instead the request is attempted, its failure is ignored, and the
 * app's own chrome comes off either way — so the control always does something useful, just more
 * of it on some devices than others.
 */
export interface FullscreenState {
  /** Whether the app's own header and navigation rail are hidden. */
  isImmersive: boolean
  /** Whether the browser actually granted true fullscreen. Often false on iOS. */
  isBrowserFullscreen: boolean
  /** Whether this browser implements the Fullscreen API at all. */
  isSupported: boolean
  enter: () => void
  exit: () => void
  toggle: () => void
}

export function useFullscreen(): FullscreenState {
  const [isImmersive, setIsImmersive] = useState(false)
  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false)

  const isSupported =
    typeof document !== 'undefined' && Boolean(document.documentElement.requestFullscreen)

  /**
   * Maps and virtualised tables measure themselves once and cache it, so hiding a header that was
   * taking sixty pixels leaves them drawing into a box that no longer exists. The layout already
   * fires this same volley when the sidebar collapses, for the same reason. Several of them,
   * because the CSS transition means the final size is not readable on the first frame.
   */
  const nudgeLayout = useCallback(() => {
    for (const delay of [50, 200, 400]) {
      window.setTimeout(() => window.dispatchEvent(new Event('resize')), delay)
    }
  }, [])

  const enter = useCallback(() => {
    setIsImmersive(true)
    nudgeLayout()

    // Ignored on purpose. The request rejects when the browser does not support it, when there was
    // no user gesture, or when a permissions policy forbids it — and in every one of those cases
    // the immersive layout above is still the right outcome. An unhandled rejection here would
    // surface as a console error on iOS every single time the button was pressed.
    document.documentElement.requestFullscreen?.().catch(() => {})
  }, [nudgeLayout])

  const exit = useCallback(() => {
    setIsImmersive(false)
    nudgeLayout()

    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {})
    }
  }, [nudgeLayout])

  const toggle = useCallback(() => {
    if (isImmersive) exit()
    else enter()
  }, [isImmersive, enter, exit])

  useEffect(() => {
    /**
     * Escape leaves browser fullscreen without telling the page, so without this the app chrome
     * would stay hidden with no visible way back and the only remaining exit would be a reload.
     */
    const onChange = () => {
      const active = Boolean(document.fullscreenElement)
      setIsBrowserFullscreen(active)

      if (!active) {
        setIsImmersive(false)
        nudgeLayout()
      }
    }

    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [nudgeLayout])

  useEffect(() => {
    /**
     * On the browsers with no Fullscreen API there is no fullscreenchange event either, so Escape
     * has to be handled directly or immersive mode has no keyboard exit at all.
     */
    if (!isImmersive || isBrowserFullscreen) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') exit()
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isImmersive, isBrowserFullscreen, exit])

  return { isImmersive, isBrowserFullscreen, isSupported, enter, exit, toggle }
}
